import { mediaQuery, pxToRem } from '@noths/polaris-client-styles';
import { css } from 'styled-components';

import { dggTheme } from 'src/styles/dggTheme';
import { breakpoint } from 'src/styles/dontgiftguide.styles';

const { spacing, fonts, colors, text } = dggTheme;

const pinkPresentURL =
  'https://images.contentstack.io/v3/assets/blt83ec2ae5e0dbde7f/blt22ccfd0cf5c4673d/present-pink.png';

const redPresentURL =
  'https://images.contentstack.io/v3/assets/blt83ec2ae5e0dbde7f/blt6f953fb474867837/present-red.png';

const singleSnowflakeURL =
  'https://images.contentstack.io/v3/assets/blt83ec2ae5e0dbde7f/blt3f6424008a571182/single-snowflake-light.png';

const singleSnowflakeDarkURL =
  'https://images.contentstack.io/v3/assets/blt83ec2ae5e0dbde7f/blt820dc1ad29c4b82a/single-snowflake-dark.png';

export const doubleSnowflakeURL =
  'https://images.contentstack.io/v3/assets/blt83ec2ae5e0dbde7f/blt49a128f72c902bf0/double-snowflake-right.png';

const doubleSnowflakeDarkURL =
  'https://images.contentstack.io/v3/assets/blt83ec2ae5e0dbde7f/blt623c039f9f30f082/double-snowflake-dark.png';

const doubleSnowflakeFlipURL =
  'https://images.contentstack.io/v3/assets/blt83ec2ae5e0dbde7f/blta8739b8443135a09/double-snowflake-left.png';

const presentX =
  'https://images.contentstack.io/v3/assets/blt83ec2ae5e0dbde7f/bltb85a981273ea77ea/present-X.png';

const headingFontSize = { mobile: 46, default: 80 };

export const group = css`
  text-align: center;
`;

export const sections = css`
  margin: 0 auto;
`;

const commonBackgroundStyles = css`
  background-repeat: no-repeat;
  background-position: right top;
`;

const backgroundImageWithSize = (url: string, size: number) => css`
  background-image: url(${url});
  background-size: ${pxToRem(size)};
`;

export const headingContainer = css`
  text-align: left;
  margin: 0 ${pxToRem(spacing.md)} ${pxToRem(spacing.xl)};

  &[data-custom-style-id='top-gifting-tips'] {
    ${backgroundImageWithSize(redPresentURL, 40)};
    background-repeat: no-repeat;
    background-position: right top ${pxToRem(30)};
  }

  &[data-custom-style-id='gifts-for-her'],
  &[data-custom-style-id='gifts-for-him'],
  &[data-custom-style-id='gifts-for-kids'] {
    ${backgroundImageWithSize(doubleSnowflakeURL, 70)};
    ${commonBackgroundStyles}
  }

  &[data-custom-style-id='christmas-eve'],
  &[data-custom-style-id='jewellery-gifts'] {
    ${backgroundImageWithSize(singleSnowflakeURL, 40)};
    ${commonBackgroundStyles}
  }

  &[data-custom-style-id='food-and-drink-gifts'] {
    background-repeat: no-repeat;
    background-position: right top ${pxToRem(50)};
    ${backgroundImageWithSize(doubleSnowflakeFlipURL, 60)};
  }

  &[data-custom-style-id='experience-gifts'] {
    background-repeat: no-repeat;
    background-position: right top ${pxToRem(50)};
    ${backgroundImageWithSize(singleSnowflakeURL, 30)};
  }

  &[data-custom-style-id='gifts-for-the-hard-to-buy-for'],
  &[data-custom-style-id='christmas-decorations'],
  &[data-custom-style-id='festive-entertaining'] {
    ${backgroundImageWithSize(singleSnowflakeURL, 30)};
    ${commonBackgroundStyles}
  }

  &[data-custom-style-id='get-cosy'] {
    ${backgroundImageWithSize(doubleSnowflakeDarkURL, 60)};
    ${commonBackgroundStyles}
  }

  &[data-custom-style-id='stocking-fillers'] {
    ${backgroundImageWithSize(singleSnowflakeDarkURL, 40)};
    background-repeat: no-repeat;
    background-position: right top ${pxToRem(25)};
  }

  &[data-custom-style-id='the-most-mis-gifted-items'] {
    h2:after {
      position: absolute;
      display: inline-block;
      width: ${pxToRem(headingFontSize.mobile * 2)};
      height: ${pxToRem(headingFontSize.mobile)};
      margin-left: ${pxToRem(spacing.sm)};
      content: '';
      transform: translateY(-4px);

      background-image: url(${presentX});
      background-size: contain;
      background-repeat: no-repeat;

      ${mediaQuery(
        { min: breakpoint },
        css`
          width: ${pxToRem(headingFontSize.default * 2)};
          height: ${pxToRem(headingFontSize.default)};
          margin-left: ${pxToRem(spacing.md)};
        `,
      )}
    }
  }

  ${mediaQuery(
    { min: breakpoint },
    css`
      margin: ${pxToRem(spacing.xxxxl)} auto;
      text-align: center;
      padding-right: ${pxToRem(spacing.md)};

      &[data-custom-style-id='top-gifting-tips'] {
        background-image: url(${pinkPresentURL}), url(${redPresentURL});
        background-repeat: no-repeat;
        background-position: left ${pxToRem(80)} top 0, right ${pxToRem(95)} top 0;
        background-size: ${pxToRem(110)};
      }

      &[data-custom-style-id='gifts-for-her'],
      &[data-custom-style-id='gifts-for-him'],
      &[data-custom-style-id='gifts-for-kids'] {
        background-position: right ${pxToRem(200)} top 0;
        background-size: ${pxToRem(110)};
      }

      &[data-custom-style-id='jewellery-gifts'],
      &[data-custom-style-id='christmas-eve'] {
        background-size: ${pxToRem(70)};
        background-position: right ${pxToRem(200)} top ${pxToRem(50)};
      }

      &[data-custom-style-id='get-cosy'] {
        background-size: ${pxToRem(80)};
        background-position: right ${pxToRem(200)} top;
      }

      &[data-custom-style-id='gifts-for-the-hard-to-buy-for'],
      &[data-custom-style-id='festive-entertaining'] {
        background-size: ${pxToRem(60)};
        background-position: right ${pxToRem(220)} top;
      }

      &[data-custom-style-id='stocking-fillers'] {
        background-size: ${pxToRem(70)};
        background-position: right ${pxToRem(200)} top ${pxToRem(20)};
      }

      &[data-custom-style-id='experience-gifts'] {
        background-position: right ${pxToRem(200)} top ${pxToRem(60)};
        background-size: ${pxToRem(70)};
      }

      &[data-custom-style-id='christmas-decorations'] {
        background-position: right ${pxToRem(200)} top ${pxToRem(10)};
        background-size: ${pxToRem(50)};
      }

      &[data-custom-style-id='food-and-drink-gifts'] {
        background-size: ${pxToRem(110)};
        background-position: right ${pxToRem(210)} top;
      }
    `,
  )}
`;

export const heading = css`
  font-size: ${pxToRem(headingFontSize.mobile)};
  line-height: ${pxToRem(headingFontSize.mobile)};
  margin: ${pxToRem(spacing.xs)} 0;
  font-family: ${fonts.PRIMARY_SEMIBOLD.familyWithFallbacks};
  font-weight: ${fonts.PRIMARY_SEMIBOLD.weight};

  ${mediaQuery(
    { min: breakpoint },
    css`
      width: ${pxToRem(540)};
      margin: ${pxToRem(spacing.xs)} auto;
      font-size: ${pxToRem(headingFontSize.default)};
      line-height: ${pxToRem(headingFontSize.default)};
      font-family: ${fonts.PRIMARY_REGULAR.familyWithFallbacks};
      font-weight: ${fonts.PRIMARY_REGULAR.weight};
    `,
  )}
`;

export const copy = css`
  margin: 0;
  font-size: ${pxToRem(text.HEADING.XS.fontSize)};
  margin: ${pxToRem(spacing.xs)} 0;

  ${mediaQuery(
    { min: breakpoint },
    css`
      width: ${pxToRem(650)};
      margin: 0 auto;
      font-size: ${pxToRem(text.HEADING.MD.fontSize)};
    `,
  )}
`;

export const categoryLink = css`
  display: none;
  font-size: ${pxToRem(text.HEADING.MD.fontSize)};
  font-family: ${fonts.PRIMARY_SEMIBOLD.familyWithFallbacks};
  font-weight: ${fonts.PRIMARY_SEMIBOLD.weight};
  text-decoration: none;
  color: inherit;
  margin: ${pxToRem(spacing.xs)} 0;

  ${mediaQuery(
    { min: breakpoint },
    css`
      display: block;
    `,
  )};
`;

export const footer = css`
  text-align: center;
  padding: ${pxToRem(spacing.md)} 0;
  background-image: url(${singleSnowflakeURL}), url(${doubleSnowflakeURL});
  background-repeat: no-repeat;
  background-position: left bottom ${pxToRem(10)}, right top ${pxToRem(25)};
  background-size: ${pxToRem(30)}, ${pxToRem(50)};

  a {
    display: block;
    margin: 0 auto ${pxToRem(spacing.sm)} auto;
  }

  ${mediaQuery(
    { min: breakpoint },
    css`
      width: 450px;
      margin: 0 auto;
    `,
  )};
`;

export const footerTitle = css`
  font-family: ${fonts.PRIMARY_SEMIBOLD.familyWithFallbacks};
  color: ${colors.neutralWhite};
`;
